import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Breadcrum from '../../../components/common/Breadcrum';
import Models from '../../../components/common/Models';
import CreateAppReleaseForm from './CreateAppReleaseForm';
import {
  fetchAppReleases,
  updateAppReleaseStatus,
  deleteAppRelease,
  clearError
} from '../../../redux/slices/appReleaseSlice';

export default function AppReleaseSuperAdmin() {
  const dispatch = useDispatch();
  const { releases = [], loading, error, updateLoading, deleteLoading } = useSelector(
    state => state.appRelease
  );

  // Ensure releases is always an array
  const safeReleases = Array.isArray(releases) ? releases : [];

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const breadcrumbItems = [
    { label: 'App Release', path: '/super-admin/app_release' }
  ];

  useEffect(() => {
    dispatch(fetchAppReleases());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await dispatch(updateAppReleaseStatus({ id, status: newStatus })).unwrap();
      toast.success('Status updated successfully!');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteRelease = async () => {
    if (!selectedRelease) return;

    try {
      await dispatch(deleteAppRelease(selectedRelease.id)).unwrap();
      toast.success('App release deleted successfully!');
      setShowDeleteConfirm(false);
      setSelectedRelease(null);
    } catch (error) {
      toast.error('Failed to delete app release');
    }
  };

  const confirmDelete = (release) => {
    setSelectedRelease(release);
    setShowDeleteConfirm(true);
  };
  console.log("App Releases:", releases); // Log releases to check if they are being fetched correctly
  return (
    <>
      <div className='bg-gray-200 dark:bg-gray-800 py-2 px-4 rounded-lg flex justify-between items-center'>
        <div>
          <h1 className='text-2xl font-semibold text-theme-dark dark:text-theme-light'>
            {breadcrumbItems[0].label}
          </h1>
          <Breadcrum items={breadcrumbItems} />
        </div>
        <div>
          <button 
            onClick={() => setIsCreateModalOpen(true)} 
            className='bg-primary-light text-white px-3 py-2 rounded-xl hover:bg-primary-dark transition-colors'
          >
            Add New Release
          </button>
        </div>
      </div>

      {/* Releases List */}
      <div className="mt-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-light"></div>
            <p className="mt-2 text-gray-600">Loading releases...</p>
          </div>
        ) : safeReleases.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">No app releases found.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {safeReleases.map((release) => (
              <div key={release.id} className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Version {release.version}
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        release.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {release.status ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-2 whitespace-pre-line">
                      {release.releaseNote}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleStatusUpdate(release.id, !release.status)}
                      disabled={updateLoading}
                      className={`px-3 py-1 text-white rounded-md text-sm disabled:opacity-50 ${
                        release.status 
                          ? 'bg-red-600 hover:bg-red-700' 
                          : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {release.status ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => confirmDelete(release)}
                      disabled={deleteLoading}
                      className="px-3 py-1 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                {release.link && (
                  <div className="mt-4">
                    <a
                      href={release.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-600 hover:text-blue-800"
                    >
                      Download Link
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                )}
                
                <div className="text-xs text-gray-500 mt-4">
                  Created: {new Date(release.createdAt || Date.now()).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Release Modal */}
      <Models 
        isOpen={isCreateModalOpen} 
        isClose={() => setIsCreateModalOpen(false)} 
        name="Create New App Release"
        size="large"
      >
        <CreateAppReleaseForm 
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            dispatch(fetchAppReleases());
          }}
        />
      </Models>

      {/* Delete Confirmation Modal */}
      <Models 
        isOpen={showDeleteConfirm} 
        isClose={() => setShowDeleteConfirm(false)} 
        name="Confirm Delete"
      >
        <div className="p-4">
          <p className="text-gray-700 mb-4">
            Are you sure you want to delete the release "Version {selectedRelease?.version}"? 
            This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={deleteLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteRelease}
              disabled={deleteLoading}
              className={`px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 ${
                deleteLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Models>
    </>
  );
}
